import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { productAPI } from '../services/api';
import { Button, Modal, StockBadge, Badge, Spinner, EmptyState, PageHeader } from '../components/Common';
import { BASE_URL } from '../config';

const UNITS = ['item','kg','meter','foot','inch','liter','gram','piece','box','dozen'];
const emptyForm = { name:'', category:'', quantity:0, unit:'item', buy_price:'', sell_price:'', low_stock_threshold:5, sku:'', description:'', hsn_code:'' };

const InventoryPage = () => {
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [categories, setCategories] = useState([]);
  const [catFilter,  setCatFilter]  = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [imageFile,  setImageFile]  = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productAPI.getAll({ search, category: catFilter });
      setProducts(res.data.products);
    } catch { toast.error('Products load nahi hue'); }
    finally { setLoading(false); }
  }, [search, catFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => {
    productAPI.getCategories().then(r => setCategories(r.data.categories)).catch(()=>{});
  }, []);

  const openAdd = () => {
    setForm(emptyForm);
    setEditId(null);
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, quantity: p.quantity,
      unit: p.unit, buy_price: p.buy_price, sell_price: p.sell_price,
      low_stock_threshold: p.low_stock_threshold, sku: p.sku||'',
      description: p.description||'',
      hsn_code: p.hsn_code||''
    });
    setEditId(p.id);
    setImageFile(null);
    setImagePreview(p.image ? BASE_URL + p.image : null);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.category || !form.buy_price || !form.sell_price) {
      toast.error('Required fields fill karo'); return;
    }
    setSaving(true);
    try {
      // FormData use karo image ke liye
      const formData = new FormData();
      Object.keys(form).forEach(key => formData.append(key, form[key]));
      if (imageFile) formData.append('image', imageFile);

      if (editId) {
        await productAPI.update(editId, formData);
        toast.success('Product update ho gaya! ✅');
      } else {
        await productAPI.create(formData);
        toast.success('Product add ho gaya! ✅');
      }
      setShowModal(false);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error aaya');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('"' + name + '" delete karna chahte ho?')) return;
    try {
      await productAPI.delete(id);
      toast.success('"' + name + '" delete ho gaya');
      loadProducts();
    } catch { toast.error('Delete nahi hua'); }
  };

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  return (
    <div className="page">
      <PageHeader
        title="📦 Inventory"
        subtitle={products.length + ' products'}
        actions={<Button onClick={openAdd}>+ New Product</Button>}
      />

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-wrap" style={{ flex:1, minWidth:200 }}>
          <span className="search-icon">🔍</span>
          <input className="form-input" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft:34 }} />
        </div>
        <select className="form-input" style={{ width:180 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : products.length === 0 ? (
            <EmptyState icon="📦" title="Koi products nahi" message="'+ New Product' se add karo" />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Image</th><th>SKU</th><th>Product</th><th>Category</th>
                  <th>Stock</th><th>Unit</th><th>Buy</th><th>Sell</th>
                  <th>Margin</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      {p.image ? (
                        <img
                          src={BASE_URL + p.image}
                          alt={p.name}
                          style={{
                            width: 44, height: 44, objectFit: 'cover',
                            borderRadius: 8, border: '1px solid var(--border)',
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 44, height: 44, borderRadius: 8,
                          background: 'var(--bg3)', border: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 20,
                        }}>📦</div>
                      )}
                    </td>
                    <td style={{ color:'var(--text3)', fontSize:11 }}>{p.sku}</td>
                    <td><strong>{p.name}</strong></td>
                    <td><Badge variant="blue">{p.category}</Badge></td>
                    <td>
                      <strong style={{ color: parseFloat(p.quantity) <= parseFloat(p.low_stock_threshold) ? 'var(--danger)' : 'var(--accent2)' }}>
                        {p.quantity}
                      </strong>
                    </td>
                    <td style={{ color:'var(--text2)' }}>{p.unit}</td>
                    <td>Rs.{parseFloat(p.buy_price).toFixed(0)}</td>
                    <td>Rs.{parseFloat(p.sell_price).toFixed(0)}</td>
                    <td>
                      <Badge variant={p.profit_margin>=30?'green':p.profit_margin>=15?'orange':'red'}>
                        {p.profit_margin}%
                      </Badge>
                    </td>
                    <td><StockBadge status={p.status} /></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>✏️</button>
                        <button className="btn btn-sm" style={{ background:'rgba(255,107,107,.15)', color:'var(--danger)' }} onClick={() => handleDelete(p.id, p.name)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editId ? '✏️ Product Edit' : '📦 New Product'} size="md">
        
        {/* Image Upload */}
        <div className="form-group">
          <label className="form-label">Product Image</label>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            {/* Preview */}
            <div style={{
              width: 80, height: 80, borderRadius: 10,
              background: 'var(--bg3)', border: '2px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', flexShrink: 0,
            }}>
              {imagePreview ? (
                <img src={imagePreview} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              ) : (
                <span style={{ fontSize: 28 }}>📷</span>
              )}
            </div>
            {/* Upload button */}
            <div>
              <label style={{
                cursor: 'pointer', display: 'inline-block',
                padding: '8px 14px', background: 'var(--bg3)',
                border: '1px solid var(--border)', borderRadius: 8,
                fontSize: 13, color: 'var(--text2)',
                transition: 'all 0.2s',
              }}>
                📁 Image Choose Karo
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
              </label>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                JPG, PNG, WEBP — Max 5MB
              </div>
              {imageFile && (
                <div style={{ fontSize: 11, color: 'var(--accent2)', marginTop: 4 }}>
                  ✅ {imageFile.name}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Product Name *</label>
            <input className="form-input" placeholder="e.g. Cotton Fabric" value={form.name} onChange={e => f('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Category *</label>
            <input className="form-input" placeholder="e.g. Textile" value={form.category} onChange={e => f('category', e.target.value)} list="cat-list" />
            <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Quantity *</label>
            <input className="form-input" type="number" step="0.01" min="0" value={form.quantity} onChange={e => f('quantity', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Unit *</label>
            <select className="form-input" value={form.unit} onChange={e => f('unit', e.target.value)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Purchase Price (Rs.) *</label>
            <input className="form-input" type="number" step="0.01" min="0" value={form.buy_price} onChange={e => f('buy_price', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Selling Price (Rs.) *</label>
            <input className="form-input" type="number" step="0.01" min="0" value={form.sell_price} onChange={e => f('sell_price', e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Low Stock Alert</label>
            <input className="form-input" type="number" min="0" value={form.low_stock_threshold} onChange={e => f('low_stock_threshold', e.target.value)} />
          </div>
          
          <div className="form-group">
            <label className="form-label">SKU (optional)</label>
            <input className="form-input" placeholder="Auto-generate hoga" value={form.sku} onChange={e => f('sku', e.target.value)} />
          </div>
          
        </div>
        <div className="form-group">
            <label className="form-label">HSN/SAC Code</label>
            <input className="form-input" placeholder="e.g. 5208" value={form.hsn_code} onChange={e => f('hsn_code', e.target.value)} />
          </div>
        {form.buy_price && form.sell_price && parseFloat(form.buy_price) > 0 && (
              <div className="alert alert-success" style={{ marginBottom:16 }}>
                💹 Profit Margin: <strong>{Math.round((form.sell_price - form.buy_price) / form.buy_price * 100)}%</strong>
                &nbsp; (Rs.{(form.sell_price - form.buy_price).toFixed(2)} per {form.unit})
              </div>
            )}

        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>💾 {editId ? 'Update' : 'Save'}</Button>
        </div>
      </Modal>
    </div>
  );
};

export default InventoryPage;